import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Modal } from 'antd'
import PropTypes from 'prop-types'
import { toPath } from '../routes'
import { useIsAdmin } from '../redux/slices/session'

const STORAGE_KEY = 'biobridge_selected_practice_id'
const RECENT_KEY = 'biobridge_recent_practice_ids'
const MAX_RECENT = 10

function getRecentPracticeIds() {
  try {
    const raw = sessionStorage.getItem(RECENT_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function addRecentPracticeId(id) {
  if (!id) return
  const recent = getRecentPracticeIds().filter(rid => rid !== id)
  recent.unshift(id)
  sessionStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

const PracticeContext = createContext()

// More specific segments must come before less specific ones
// (e.g. 'office-billing-orders' before 'billing-orders', 'orders-tabs' before 'orders')
const PRACTICE_ROUTE_TO_LISTING = [
  { segment: '/missed-transmissions', listing: () => toPath.missedTransmissions() },
  { segment: '/office-billing-orders', listing: () => toPath.billing.officeBillingOrders() },
  { segment: '/billing-orders', listing: () => toPath.billing.remoteBillingOrders() },
  { segment: '/pre-authorizations', listing: () => toPath.preAuthorizations() },
  { segment: '/office-authorizations', listing: () => toPath.billing.officeAuthorizations() },
  { segment: '/office-checks', listing: () => toPath.officeChecks() },
  { segment: '/cycles', listing: () => toPath.cycles() },
  { segment: '/reports', listing: () => toPath.reports() },
  { segment: '/orders-tabs', listing: () => toPath.orders() },
  { segment: '/uploads', listing: () => toPath.practices.uploads() },
]

// Routes not under /practices/ that should also redirect on practice change
const STANDALONE_ROUTE_TO_LISTING = [
  { match: /^\/transmissions\//, listing: () => toPath.transmissions() },
]

function getListingRoute(pathname) {
  if (pathname.includes('/practices/')) {
    const practiceMatch = PRACTICE_ROUTE_TO_LISTING.find(({ segment }) =>
      pathname.includes(segment),
    )
    if (practiceMatch) return practiceMatch.listing()
  }
  const standaloneMatch = STANDALONE_ROUTE_TO_LISTING.find(({ match }) =>
    match.test(pathname),
  )
  return standaloneMatch ? standaloneMatch.listing() : null
}

export function PracticeProvider({ children }) {
  const [selectedPracticeId, setSelectedPracticeIdState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null,
  )
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = useIsAdmin()
  const [recentPracticeIds, setRecentPracticeIds] = useState(getRecentPracticeIds)

  const setSelectedPracticeId = useCallback((id) => {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id)
      addRecentPracticeId(id)
      setRecentPracticeIds(getRecentPracticeIds())
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
    setSelectedPracticeIdState(id)
  }, [])

  const setSelectedPracticeWithConfirm = useCallback((id) => {
    if (!id || !isAdmin) {
      setSelectedPracticeId(id)
      return
    }
    const listingRoute = getListingRoute(location.pathname)
    if (listingRoute && selectedPracticeId && id !== selectedPracticeId) {
      Modal.confirm({
        title: 'Change practice?',
        content: 'Changing the practice will redirect you to the listing page. Continue?',
        okText: 'Yes, change practice',
        cancelText: 'Cancel',
        onOk: () => {
          setSelectedPracticeId(id)
          navigate(listingRoute)
        },
      })
    } else {
      setSelectedPracticeId(id)
    }
  }, [isAdmin, location.pathname, selectedPracticeId, setSelectedPracticeId, navigate])

  const value = useMemo(
    () => ({
      selectedPracticeId,
      setSelectedPracticeId: setSelectedPracticeWithConfirm,
      recentPracticeIds,
    }),
    [selectedPracticeId, setSelectedPracticeWithConfirm, recentPracticeIds],
  )

  return (
    <PracticeContext.Provider value={value}>
      {children}
    </PracticeContext.Provider>
  )
}

PracticeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export function useSelectedPractice() {
  const context = useContext(PracticeContext)
  if (!context) {
    return { selectedPracticeId: null, setSelectedPracticeId: () => {}, recentPracticeIds: [] }
  }
  return context
}
